import { DEFAULT_CHECKLIST, type AccessModule } from "@bridewell/shared";
import {
  Activity, Armchair, CalendarDays, Camera, CheckSquare, ClipboardList, CreditCard, Gift, Grid3X3, Hourglass, House, Mail, ShoppingBag,
  StickyNote, Store, Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { t, type TKey } from "@/i18n";
import { t as marketingT } from "./marketing-copy";

/** The eleven sections, in the app's own order, with the tints the module pages use. */
export interface ModuleMeta {
  id: AccessModule;
  Icon: ComponentType<{ className?: string }>;
  /** CSS variable — theme tokens only. */
  tint: string;
  blurb: TKey;
}

export const MODULES: ModuleMeta[] = [
  { id: "overview", Icon: House, tint: "var(--accent)", blurb: "marketing.modules.overview" },
  { id: "checklist", Icon: CheckSquare, tint: "var(--success)", blurb: "marketing.modules.checklist" },
  { id: "guests", Icon: Users, tint: "var(--lavender)", blurb: "marketing.modules.guests" },
  { id: "budget", Icon: CreditCard, tint: "var(--gold)", blurb: "marketing.modules.budget" },
  { id: "vendors", Icon: Store, tint: "var(--dusty-blue)", blurb: "marketing.modules.vendors" },
  { id: "timeline", Icon: CalendarDays, tint: "var(--hero-2)", blurb: "marketing.modules.timeline" },
  { id: "seating", Icon: Grid3X3, tint: "var(--lavender)", blurb: "marketing.modules.seating" },
  { id: "notes", Icon: StickyNote, tint: "var(--warning)", blurb: "marketing.modules.notes" },
  { id: "inspiration", Icon: Camera, tint: "var(--blush)", blurb: "marketing.modules.inspiration" },
  { id: "shopping", Icon: ShoppingBag, tint: "var(--dusty-blue)", blurb: "marketing.modules.shopping" },
  { id: "registry", Icon: Gift, tint: "var(--gold)", blurb: "marketing.modules.registry" },
  { id: "activity", Icon: Activity, tint: "var(--success)", blurb: "marketing.modules.activity" },
];

/** The sections shown with a live demo in the tour (home + features pages). */
export const TOUR_IDS = ["checklist", "guests", "budget", "seating", "timeline", "vendors"] as const;
export type TourId = (typeof TOUR_IDS)[number];

/**
 * The free planning tools (features/marketing/tools/): public pages built from the app's own seeds and rules. Footer,
 * features page and every tool page link them from here; `section` is the features-page section that links to it.
 * Labels are read at render, in the language of the page on screen.
 */
export const TOOLS = [
  { id: "checklist", to: "/wedding-checklist", Icon: CheckSquare, section: "checklist", nav: () => t("marketing.tools.checklist.nav"), blurb: () => t("marketing.tools.checklist.blurb", { n: DEFAULT_CHECKLIST.length }), featuresLink: () => t("marketing.tools.checklist.featuresLink") },
  { id: "budget", to: "/wedding-budget-calculator", Icon: CreditCard, section: "budget", nav: () => t("marketing.tools.budget.nav"), blurb: () => t("marketing.tools.budget.blurb"), featuresLink: () => t("marketing.tools.budget.featuresLink") },
  { id: "timeline", to: "/wedding-day-timeline", Icon: CalendarDays, section: "timeline", nav: () => t("marketing.tools.timeline.nav"), blurb: () => t("marketing.tools.timeline.blurb"), featuresLink: () => t("marketing.tools.timeline.featuresLink") },
  { id: "seating", to: "/wedding-seating-chart", Icon: Armchair, section: "seating", nav: () => marketingT("tools.seating.nav"), blurb: () => marketingT("tools.seating.blurb"), featuresLink: () => marketingT("tools.seating.featuresLink") },
  { id: "guestList", to: "/wedding-guest-list-template", Icon: ClipboardList, section: "guests", nav: () => marketingT("tools.guestList.nav"), blurb: () => marketingT("tools.guestList.blurb"), featuresLink: () => marketingT("tools.guestList.featuresLink") },
  { id: "invitations", to: "/when-to-send-wedding-invitations", Icon: Mail, section: null, nav: () => marketingT("tools.invitations.nav"), blurb: () => marketingT("tools.invitations.blurb"), featuresLink: () => marketingT("tools.invitations.nav") },
  { id: "countdown", to: "/wedding-countdown", Icon: Hourglass, section: null, nav: () => marketingT("tools.countdown.nav"), blurb: () => marketingT("tools.countdown.blurb"), featuresLink: () => marketingT("tools.countdown.nav") },
] as const;
export type ToolId = (typeof TOOLS)[number]["id"];

/** App Store listing of the Apple apps (App Store id in CLAUDE.md). A plain link — no Apple script or badge asset is bundled. */
export const APP_STORE_URL = "https://apps.apple.com/app/id6770368982";

/** Demo couple: the seed wedding, so the marketing matches what `npm run db:seed` shows. */
export const DEMO_COUPLE = { one: "Elijah", two: "Lara" } as const;
